import { AuthData } from 'graphql/AuthQueries'
import { createContext, useContext, useReducer } from 'react'
import { PlatformData } from '_platform'
import { PlatformContext } from 'PlatformContext'
import { Tracker, TrackerUser } from 'Tracker'
import Cookies from 'universal-cookie'

enum AuthActionType {
  LOGIN,
  NEW_API_KEY,
  LOGOUT,
  LET_LOGIN
}

interface AuthAction {
  type: AuthActionType
  platform: PlatformData
  payload?: AuthData
}

interface AuthContextData extends AuthData {
  canLetLogin: Boolean
  login(authData: AuthData): void
  newApiKey(apiKey: string): void
  logout(): void
  letLogin(): void
}

const initialState: AuthContextData = {
  user: null,
  connection: null,
  level: 0,
  canLetLogin: false,
  login: (authData: AuthData) => {},
  newApiKey: (apiKey: string) => {},
  logout: () => {},
  letLogin: () => {}
}

const AuthContext = createContext<AuthContextData>({
  user: null,
  connection: null,
  level: 0,
  canLetLogin: false,
  login: (authData: AuthData) => {},
  newApiKey: (apiKey: string) => {},
  logout: () => {},
  letLogin: () => {}
})

function authReducer(state: AuthContextData, action: AuthAction): AuthContextData {
  switch(action.type){
    case AuthActionType.LOGIN:
      if(action.payload){
        action.platform.authService.storeAuthData(action.payload);

        if(action.payload.roles?.includes("NoTracking")){
          const cookies = new Cookies();
          cookies.set("disableAnalytics", "true", { path: '/' });
        }

        const trackerUser: TrackerUser = {
          id: action.payload.user?.id || null,
          level: action.payload.level
        }

        Tracker.event("action.login.user", trackerUser, {
          login: {
            method: "password"
          }
        })
      }
      return {
        ...state,
        ...action.payload
      }
    case AuthActionType.NEW_API_KEY:
      return {
        ...state,
        apiToken: action.payload?.apiToken
      }
    case AuthActionType.LOGOUT:
      action.platform.authService.deleteAuthData();

      const trackerUser: TrackerUser = {
        id: state.user?.id || null,
        level: state.level
      }

      Tracker.event("action.logout.user", trackerUser, null)
      return {
        ...state,
        user: null
      }
    case AuthActionType.LET_LOGIN:
      return {
        ...state,
        canLetLogin: true
      }
    default:
      return state;
  }
}

function AuthProvider(props: any) {
  const platform = useContext(PlatformContext);

  initialState.canLetLogin = !platform.isEmbedded;

  const [state, dispatch] = useReducer(authReducer, initialState);

  const login = (authData: AuthContextData) => {
    dispatch({
      type: AuthActionType.LOGIN,
      platform: platform,
      payload: authData
    })
  }

  const newApiKey = (apiKey: string) => {
    dispatch({
      type: AuthActionType.NEW_API_KEY,
      platform: platform,
      payload: { user: null, connection: null, level: 0, apiToken: apiKey }
    })
  }

  function logout() {
    dispatch({
      type: AuthActionType.LOGOUT,
      platform: platform
    })
  }

  function letLogin() {
    dispatch({
      type: AuthActionType.LET_LOGIN,
      platform: platform
    })
  }

  return (
    <AuthContext.Provider
      value={{user: state.user, connection: state.connection, level: state.level, apiToken: state.apiToken, roles: state.roles, canLetLogin: state.canLetLogin,
        letLogin: letLogin, login, newApiKey, logout}}
      {...props}
    />
  )
}

export { AuthContext, AuthProvider };
